import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/utils/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

const inp = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#159DFF]";
const first = (inv, keys) => { for (const k of keys) if (inv?.[k] != null && inv[k] !== "") return inv[k]; return ""; };

const FIELDS = [
  { key: "customer_name", label: "Bill-to name", keys: ["customer_name"] },
  { key: "customer_address", label: "Bill-to address", keys: ["customer_address", "bill_to_address"], area: true },
  { key: "customer_phone", label: "Phone", keys: ["customer_phone"] },
  { key: "customer_email", label: "Email", keys: ["customer_email"] },
  { key: "origin_contact", label: "Ship-from contact", keys: ["origin_contact"] },
  { key: "origin_address", label: "Ship-from address", keys: ["origin_address", "ship_from_address"], area: true },
  { key: "dest_contact", label: "Ship-to contact", keys: ["dest_contact"] },
  { key: "dest_address", label: "Ship-to address", keys: ["dest_address", "ship_to_address"], area: true },
];

const label = (k) => (FIELDS.find((f) => f.key === k) || {}).label || k;

export default function InvoiceEditDialog({ invoice, onClose, onSaved }) {
  const [vals, setVals] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!invoice) return;
    const v = {}; FIELDS.forEach((f) => { v[f.key] = first(invoice, f.keys); });
    setVals(v); setReason("");
    api.get(`/v2/invoice-edit/${invoice.invoice_number}/history`).then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
  }, [invoice]);

  if (!invoice) return null;
  const original = (f) => first(invoice, f.keys);
  const changed = FIELDS.filter((f) => (vals[f.key] ?? "").trim() !== String(original(f)).trim());

  const save = async () => {
    if (!changed.length) { toast.info("Nothing changed"); return; }
    setBusy(true);
    try {
      const body = { reason };
      changed.forEach((f) => { body[f.key] = vals[f.key]; });
      await api.patch(`/v2/invoice-edit/${invoice.invoice_number}/details`, body);
      toast.success("Invoice details updated");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save changes");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="invoice-edit-dialog">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Edit invoice details</h2>
            <p className="text-xs text-slate-500">{invoice.invoice_number} · names, addresses and contacts only. Amounts, GSTIN and tax stay locked.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 grid sm:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <label key={f.key} className={`block ${f.area ? "sm:col-span-2" : ""}`}>
              <span className="text-xs font-semibold text-slate-600">{f.label}{changed.includes(f) && <span className="ml-1 text-amber-600">• edited</span>}</span>
              {f.area
                ? <textarea rows={2} className={inp} value={vals[f.key] ?? ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} data-testid={`edit-${f.key}`} />
                : <input className={inp} value={vals[f.key] ?? ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} data-testid={`edit-${f.key}`} />}
            </label>
          ))}
          <label className="block sm:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Reason for change (optional, kept in the history)</span>
            <input className={inp} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer asked to correct company name" />
          </label>
        </div>

        {history.length > 0 && (
          <div className="px-5 pb-4">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Edit history</p>
            <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100 text-xs">
              {history.map((h) => (
                <div key={h.id} className="p-2">
                  <p className="text-slate-500">{new Date(h.at).toLocaleString()} · <b className="text-slate-700">{h.by}</b> ({h.by_role}){h.reason ? ` — ${h.reason}` : ""}</p>
                  {(h.changes || []).map((c, i) => (
                    <p key={i} className="text-slate-700"><b>{label(c.field)}:</b> <span className="line-through text-slate-400">{c.old || "—"}</span> → {c.new}</p>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !changed.length} data-testid="invoice-edit-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Save ${changed.length || ""} change${changed.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
